import { IntegrationCard } from '@components'

import {
  AlignItems,
  Button,
  Dropzone,
  FilesPreview,
  FileType,
  getRejectedFilesList,
  notify,
  Text,
} from '@billy/design-system'
import { ReactElement } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { UploadFilesErrorMessage } from '@features/attachments/elements/UploadFilesErrorMessage'

import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { formatMegabytesToBytes } from '../../../../../../utils/formatMegabytesToBytes'
import { getImageClassName } from '../../../../../../utils/getClassName'
import * as Styled from './styles'

const MAX_FILE_SIZE_MB = 5
const MAX_NUM_FILES = 1
const PDF_ASPECT_RATIO = '1/1.41'

interface FileDropZoneProps {
  fileUrl?: string
  fileType?: FileType
  handleDeleteButtonClick: () => void
  isProcessing: boolean
  onDropAccepted: (files: File[]) => void
  uploadTitle: string
}

export const FileDropZone = ({
  fileUrl,
  fileType,
  handleDeleteButtonClick,
  isProcessing,
  onDropAccepted,
  uploadTitle,
}: FileDropZoneProps): ReactElement | null => {
  const { t } = useTranslation()

  const handleDropRejected = (filesRejected: FileRejection[]) => {
    const uploadFilesErrors = getRejectedFilesList(filesRejected)
    notify({
      id: NotificationKeys.EInvoiceKYCUploadFileError,
      message: UploadFilesErrorMessage({ uploadFilesErrors }),
      title: t('file.rejected.count', { count: filesRejected.length }),
      variant: 'error',
    })
  }

  if (fileUrl && fileType) {
    return (
      <IntegrationCard>
        <Styled.FilePreviewContainer>
          <FilesPreview
            alignment={AlignItems.Center}
            aspectRatio={fileType === 'pdf' ? PDF_ASPECT_RATIO : undefined}
            bordered
            className={getImageClassName()}
            asType={fileType}
            files={[{ src: fileUrl }]}
            fitToHeight
            withModalZoom
            withDownloadButton={false}
            withRotate={false}
          />
        </Styled.FilePreviewContainer>
        <Button onClick={handleDeleteButtonClick} variant="text">
          {t('e_invoice.kyc.step.upload.dropzone.button')}
        </Button>
      </IntegrationCard>
    )
  }

  return (
    <IntegrationCard>
      <Dropzone
        contentText={uploadTitle}
        footerText={t('attachments.upload_button')}
        isUploading={isProcessing}
        maxFiles={MAX_NUM_FILES}
        maxSize={formatMegabytesToBytes(MAX_FILE_SIZE_MB)}
        onDropAccepted={onDropAccepted}
        onDropRejected={handleDropRejected}
      />
      <Text alignment="center" variant="bodySM">
        {t('e_invoice.kyc.step.upload.dropzone.footer')}
      </Text>
    </IntegrationCard>
  )
}
